import { logger } from '../utils/logger';

const HTTP_UNAUTHORIZED = 401;
const SSE_DATA_PREFIX = 'data: ';

// API base URLs - use proxy paths that are configured in setupProxy.js
const API_BASE_URLS = {
  manager: '/api/tenant',
  data: '/api/data-agent'
};

function authHeaders(extra = {}) {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

// ── Session-expiry signal ─────────────────────────────────────────────
// A single window event ('auth:expired') is dispatched whenever any API
// response returns 401. AuthContext listens for it, clears auth state,
// and shows the SessionExpiredModal so the user can sign in again
// without losing their place.
let _expiredDispatched = false;
function notifySessionExpired() {
  // De-dupe: many in-flight requests can return 401 in rapid succession.
  if (_expiredDispatched) {return;}
  _expiredDispatched = true;
  try {
    window.dispatchEvent(new CustomEvent('auth:expired', {
      detail: { resumeUrl: window.location.pathname + window.location.search },
    }));
  } catch {
    /* SSR / no window */
  }
}
export function resetSessionExpiredFlag() { _expiredDispatched = false; }

// Endpoints that should NOT trigger the expired modal (login returns 401 for bad creds).
const EXPIRY_BYPASS_PATTERNS = [
  /\/auth\/login(\?|$)/,
  /\/auth\/register(\?|$)/,
  /\/auth\/verify-email(\?|$)/,
  /\/auth\/resend-verification(\?|$)/,
];
function shouldNotifyOn401(url) {
  return !EXPIRY_BYPASS_PATTERNS.some((re) => re.test(url));
}

// Reshape a parsed error body's detail/message/error field into a display string.
// Array bodies (e.g. FastAPI validation errors) are joined; non-strings are
// stringified; everything else (including a missing field) falls back to `fallback`.
function normalizeErrorDetail(body, fallback) {
  const raw = body && (body.detail || body.message || body.error);
  if (!raw) {return fallback;}
  if (Array.isArray(raw)) {return raw.map(d => d.msg || JSON.stringify(d)).join('; ');}
  if (typeof raw !== 'string') {return JSON.stringify(raw);}
  return raw;
}

async function extractErrorDetail(response) {
  const fallback = `HTTP ${response.status}`;
  try {
    const body = await response.json();
    return normalizeErrorDetail(body, fallback);
  } catch {
    /* response had no JSON body */
    return fallback;
  }
}

async function buildErrorFromResponse(response) {
  // Notify session expiry on 401 before consuming the body.
  if (response.status === HTTP_UNAUTHORIZED && shouldNotifyOn401(response.url)) {
    notifySessionExpired();
  }
  const detail = await extractErrorDetail(response);
  const err = new Error(detail);
  err.status = response.status;
  err.detail = detail;
  return err;
}

/**
 * Parse SSE (Server-Sent Events) stream
 * @param {ReadableStream} stream - The response body stream
 * @param {Function} onMessage - Callback for each message
 * @param {Function} onError - Callback for errors
 * @param {Function} onComplete - Callback when stream completes
 */
// Fire onComplete at most once across the lifetime of a single parseSSEStream call.
function callOnCompleteOnce(onComplete, state) {
  if (!onComplete || state.completeCalled) {return;}
  state.completeCalled = true;
  onComplete();
}

// Handle one line of a decoded SSE chunk. Returns true when the line was the
// terminal "[DONE]" sentinel, signalling the caller to stop reading.
function handleSSELine(line, onMessage, onError) {
  if (!line.startsWith(SSE_DATA_PREFIX)) {return false;}
  const data = line.slice(SSE_DATA_PREFIX.length);
  if (data === '[DONE]') {return true;}

  try {
    const parsed = JSON.parse(data);
    if (onMessage) {onMessage(parsed);}
  } catch (e) {
    logger.error('Error parsing SSE data:', e);
    if (onError) {onError(e);}
  }
  return false;
}

// Process every buffered line; returns true if the "[DONE]" sentinel was seen.
function processBufferedLines(lines, onMessage, onError) {
  for (const line of lines) {
    if (handleSSELine(line, onMessage, onError)) {return true;}
  }
  return false;
}

export async function parseSSEStream(stream, onMessage, onError, onComplete) {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const state = { completeCalled: false };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        callOnCompleteOnce(onComplete, state);
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      if (processBufferedLines(lines, onMessage, onError)) {
        callOnCompleteOnce(onComplete, state);
        return;
      }
    }
  } catch (error) {
    logger.error('Stream reading error:', error);
    if (onError) {onError(error);}
  }
}

/**
 * Make a POST request that returns SSE stream
 */
export async function streamRequest(service, endpoint, data, onMessage, onError, onComplete) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw await buildErrorFromResponse(response);
    }

    await parseSSEStream(response.body, onMessage, onError, onComplete);
  } catch (error) {
    logger.error('Stream request error:', error);
    if (onError) {onError(error);}
  }
}

/**
 * Make a regular POST request
 */
export async function postRequest(service, endpoint, data, options = {}) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  return response.json();
}

/**
 * Make a GET request
 */
export async function getRequest(service, endpoint, params = {}) {
  const baseUrl = API_BASE_URLS[service];
  const queryString = new URLSearchParams(params).toString();
  const url = `${baseUrl}${endpoint}${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  return response.json();
}

/**
 * Make a PUT request
 */
export async function putRequest(service, endpoint, data) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  return response.json();
}

/**
 * Make a DELETE request
 */
export async function deleteRequest(service, endpoint, data) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  return response.json();
}

export default {
  streamRequest,
  postRequest,
  getRequest,
  putRequest,
  deleteRequest,
  parseSSEStream
};
